import { log2 } from "./utils/log2";
import { sendSocketMessage, socketMessageStream } from "./utils/socketStream";
import store from "./store";
import _ from "lodash";
import { incomingChatSound, newConversationSound, newMessageSound } from "./utils/notificationSounds";
import { systemMessageStream, SystemMessageTypes } from "./utils/systemPubSub";
import { filter, first, map, timeout } from "rxjs/operators";
import router from "./router";
import { message } from "ant-design-vue";
import { i18n } from "./language";
import feature from "./utils/SiteFeatures";
import StringUtils from "./utils/StringUtils";

let log = log2("ChatWatcher");
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let singletonInstance = null;

const COMMANDS = {
    WATCH_CONTACTS: "user watch-contacts",
    USER_START_CHAT: "user start-chat",
    TRANSLATE_MESSAGE: "user translate-message",
};

const RESPONSE_COMMANDS = {
    CONVERSATION_CLOSED: "conversation_destroyed",
    NEW_CHAT_STARTED: "new_chat_started",
    CONVERSATION_ASSIGNED: "conversation_add",
    REFRESH_CONTACT: "refresh_contact",
    MESSAGE: "message",
    VIEWED_CONTACT: "viewed_contact",
    AGENT_JOINED_CHAT: "agent_added_to_chat",
    AGENT_LEAVE_CHAT: "agent_leave_from_chat",
    VISITOR_UPDATED: "visitor_updated",
    JOIN_CHAT_IMMEDIATELY: "join_chat_immediately",
    CHAT_AGENTS_CHANGED: "chat_agents_changed",
    WHATSAPP_CONTACT_STATUS: "whatsapp_contact_status",
    CONTACT_VISIT_PAGE_CHANGED: "visitor_url_changed",
    OWNER_UPDATED: "owner_updated",
    NOTE_ADDED: "note_added",
    NOTE_DELETED: "note_deleted",
    CONVERSATIONS_SUBSCRIBED: "conversations_subscribed",
};
const autoRefreshTemplates = [
    "my-active-inbox",
    "my-contacts",
    "my-all-inbox",
    "all-agent-inbox",
    "all-inbox",
    "active-and-queue-inbox",
    "my-departments-active-inbox",
    "whatsapp-active-inbox",
    "social-media-active-inbox",
];
const chatSubscribeCheck = {};
let lastWatchContactConversationsTimerId = null;
const watchContactConversations = (ids) => {
    if (lastWatchContactConversationsTimerId) clearTimeout(lastWatchContactConversationsTimerId);
    lastWatchContactConversationsTimerId = setTimeout(() => {
        const user = store.state.auth.user;
        const site = store.state.site.site;
        const filters = store.getters["chat/getFittedContactListFilters"];
        const filterHash = StringUtils.hashCodeObj(filters);
        if (!ids || ids.length === 0) {
            chatSubscribeCheck["requestId"] = filterHash;
            console.log("chatSubscribeCheck on request", chatSubscribeCheck);
        }
        sendSocketMessage(COMMANDS.WATCH_CONTACTS, {
            siteId: site._id,
            userId: user._id,
            filters,
            ids,
            filterHash,
        });
    }, 250);
};
const retryFetch = async (fn, retryCount, maxRetryCount = 0, laterTry) => {
    if (retryCount >= maxRetryCount) return;
    return fn().catch((err) => {
        log.debug("retryFetch", { err, retryCount });
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                retryFetch(fn, retryCount + 1, maxRetryCount, laterTry)
                    .then(resolve)
                    .catch(reject);
            }, laterTry * 1000);
        });
    });
};

let lastRequestId = 0;
const fetchContactList = async (concat = false, retryCount = 1) => {
    const requestId = lastRequestId + 1;
    lastRequestId = requestId;
    const manualChatStartedContactIds = store.state.chat.manualChatStartedContactIds;
    if (concat) {
        await store
            .dispatch(
                "chat/setContacts",
                store.state.chat.contacts.filter((c) => manualChatStartedContactIds.includes(c._id))
            )
            .then(_.noop)
            .catch(console.error);
        const activeConversationContacts = store.state.chat.contacts.filter(
            (contact) => contact.lastConversation.status !== "destroyed"
        );
        watchContactConversations([activeConversationContacts.map((c) => c.lastConversation._id)]);
    }

    const fn = () => store.dispatch("chat/getConversationList", !concat ? 0 : undefined).catch(console.error);
    store.dispatch("chat/setContactLoading", true).then(_.noop).catch(console.error);
    const res = await retryFetch(fn, 0, retryCount, 3);
    if (lastRequestId !== requestId) {
        log.debug("fetchContactList -> request canceled", { requestId, lastRequestId });
        store.dispatch("chat/setContactLoading", false).then(_.noop).catch(console.error);
        return;
    }
    if (store.state.chat.activeContact) {
        await store
            .dispatch("chat/getContactById", {
                contactId: store.state.chat.activeContact._id,
                cache: false,
                updateList: false,
            })
            .then(_.noop)
            .catch(console.error);
    }

    const watchContactList = (contacts) => {
        const ids = contacts
            .filter((c) => !["destroyed"].includes(c.lastConversation.status))
            .map((c) => c.lastConversation._id);
        watchContactConversations(ids);
    };
    let items = res.data;
    if (concat) {
        const oldItems = store.state.chat.contactLists;
        const contacts = [...oldItems, ...items];
        watchContactList(contacts);
        store.dispatch("chat/setContactList", contacts).then(_.noop).catch(console.error);
    } else {
        const isManuelJoinChatAction = store.state.chat.manuelJoinChat;
        if (isManuelJoinChatAction) {
            const contactLists = store.state.chat.contactLists;
            items = [...contactLists.filter((c) => manualChatStartedContactIds.includes(c._id)), ...items];
            items = _.chain(items)
                .groupBy("_id")
                .map((items) => _.maxBy(items, "lastInteractionDate"))
                .value();
        }
        watchContactList(items);
        store.dispatch("chat/setContactList", items).then(_.noop).catch(console.error);
    }
    store.dispatch("chat/setContactLoading", false).then(_.noop).catch(console.error);
};

/**
 * Default filter modda stats'lar contactList üzerinden hesaplanır
 */
const getContactStats = () => {
    const isDefaultFilter =
        store.state.chat.contactListFilterMode === "custom" &&
        ["all-agent-inbox", null].includes(store.state.chat.selectedCustomFilterTemplate);
    if (isDefaultFilter) {
        log.debug("getContactStats disabled in default filter mode");
        return;
    }
    store.dispatch("chat/getContactListStats").then(_.noop).catch(console.error);
};

let contactListTimerId = null;
let statTimerId = null;
const fetchContactListDebounced = (concat = false, retryCount = 1, delay = 500) => {
    if (contactListTimerId) clearTimeout(contactListTimerId);
    if (delay === 0) return fetchContactList(concat, retryCount);
    else {
        contactListTimerId = setTimeout(() => {
            fetchContactList(concat, retryCount).then(_.noop).catch(console.error);
        }, delay);
    }
};
const fetchStatsDebounced = (delay = 500) => {
    if (statTimerId) clearTimeout(statTimerId);
    if (delay === 0) return getContactStats();
    else {
        statTimerId = setTimeout(() => {
            getContactStats();
        }, delay);
    }
};

let lastPlayTime = {};
const playSound = (type) => {
    log.debug("playSound", type);
    const userInteracted = store.state.app.userInteracted;
    if (!userInteracted) return;
    if (lastPlayTime[type]) clearTimeout(lastPlayTime[type]);
    const sounds = {
        [RESPONSE_COMMANDS.MESSAGE]: newMessageSound(),
        [RESPONSE_COMMANDS.NEW_CHAT_STARTED]: newConversationSound(),
        [RESPONSE_COMMANDS.CONVERSATION_ASSIGNED]: incomingChatSound(),
    };
    const sound = sounds[type];
    lastPlayTime[type] = setTimeout(() => {
        if (sound) sound.play();
    }, 1000);
};

export default class ChatWatcher {
    constructor() {
        this.listeners = [];
        this.inited = false;
        this.tabVisible = true;
        this.lastTabHiddenTime = null;
        this.totalUnreadCount = 0;
        this.init();
        fetchStatsDebounced(0);
        fetchContactList(false, 50).then(_.noop).catch(console.error);

        setInterval(async () => {
            const route = router.currentRoute;
            const isChatRoute = route.name === "chat";
            const isTabVisible = store.state.app.tabVisible;
            if (isChatRoute && isTabVisible) fetchStatsDebounced(0);
        }, 30 * 1000);
    }

    static create() {
        if (!singletonInstance) singletonInstance = new ChatWatcher();
        return singletonInstance;
    }

    updateLocalStats() {
        const stats = store.state.chat.stats;
        const isDefaultFilter = store.getters["chat/isDefaultFilter"];
        if (isDefaultFilter) {
            const { free, waiting, late, total, totalActive, chatbot, usersChatCounts } = stats;
            const contactList = store.state.chat.contactLists;
            const newStats = {
                all: totalActive,
                unread: contactList.filter((c) => c.unreadedCount > 0).length,
            };
            store.dispatch("chat/setContactListStats", newStats).then(_.noop).catch(console.error);
        }
    }

    init() {
        this.inited = true;

        store.watch(
            (state) => state.chat.stats,
            (stats) => {
                if (stats) {
                    this.updateLocalStats();
                }
            }
        );

        store.watch(
            (state) => state.socketStatus.socket_connected,
            (socketConnected) => {
                if (socketConnected) {
                    this.watchContactConversations();
                    const connectionCount = store.state.socketStatus.connection_count;
                    const validTemplates = autoRefreshTemplates;
                    const selectedCustomFilterTemplate = store.state.chat.selectedCustomFilterTemplate;
                    const isCustomMode = store.state.chat.contactListFilterMode === "custom";
                    const isValidTemplate = validTemplates.includes(selectedCustomFilterTemplate);
                    if ((isValidTemplate || !isCustomMode) && connectionCount > 0) {
                        this.reSync();
                    }
                }
            }
        );
        store.watch(
            (state) => state.chat.contactLists,
            () => {
                const activeContact = store.state.chat.activeContact;
                if (!activeContact) return;
                const contact = store.state.chat.contactLists.find((c) => c._id === activeContact._id);
                if (contact) {
                    store.dispatch("chat/setActiveContact", contact).then(_.noop).catch(console.error);
                }
                this.updateLocalStats();
            },
            { deep: true }
        );
        store.watch(
            (state) => state.chat.activeContact,
            () => {
                const activeContact = store.state.chat.activeContact;
                const showUnread = store.state.chat.contactListFilters.showUnread;
                if (!activeContact) return;
                const users = activeContact.lastConversation.users;
                const authUser = store.state.auth.user;
                const isWatching = store.state.chat.watchingContactIds.includes(activeContact._id);
                if (users.includes(authUser._id) && isWatching) {
                    store.dispatch("chat/removeContactWatchList", activeContact).then(_.noop).catch(console.error);
                }
                if (showUnread || activeContact.unreadedCount > 0) {
                    fetchStatsDebounced(100);
                }
                const isDestroyed = activeContact.lastConversation.status === "destroyed";
                if (!isDestroyed) {
                    watchContactConversations([activeContact.lastConversation._id]);
                }
                this.updateLocalStats();
            },
            { deep: true }
        );

        store.watch(
            (state) => state.chat.contactPaginationCursor.offset,
            (value, oldValue) => {
                console.log("contactPaginationCursor.offset change", value, oldValue);
                const isEq = _.isEqual(value, oldValue);
                if (isEq) return;
                fetchContactListDebounced(true, 1, 0).then(_.noop).catch(console.error);
            }
        );

        store.watch(
            (state, getters) => getters["chat/getFittedContactListFilters"],
            (newValue, oldValue) => {
                this.filtersChanged(newValue, oldValue);
            },
            { deep: true }
        );

        // store.watch(
        //     (state) => state.app.tabVisible,
        //     (visible) => {
        //         if (visible) {
        //             fetchStatsDebounced(0);
        //             if (this.lastTabHiddenTime) {
        //                 const tookTime = Date.now() - this.lastTabHiddenTime;
        //                 if (tookTime > 1000 * 60 * 5) {
        //                     fetchContactListDebounced(false, 3, 0);
        //                 }
        //             }
        //         } else {
        //             this.lastTabHiddenTime = Date.now();
        //         }
        //     },
        //     { immediate: true }
        // );

        this.initKeyListeners();
        this.initListeners();
        log.debug("ChatWatcher inited.");
    }

    filtersChanged(newValue, oldValue) {
        this.watchContactConversations();
        fetchContactListDebounced(false, 1, 0).then(_.noop).catch(console.error);
        fetchStatsDebounced(0);
    }

    watchContactConversations(ids) {
        watchContactConversations(ids);
    }

    initKeyListeners() {
        const escListener = (e) => {
            const route = router.currentRoute;
            const activeContact = store.state.chat.activeContact;
            const isChatRoute = route.name === "chat";
            const isTabChat = !route.query.tabIndex || route.query.tabIndex === "0";
            // ekrana resim gösteren component kapandığını iletmediği için bu kontrolü yapmak zorundayız
            const showingImage = document.querySelector(
                "body > div.viewer-container.viewer-backdrop.viewer-fixed.viewer-fade.viewer-transition.viewer-in"
            );
            const windows = store.state.app.windows;
            const isHaveWindows = Object.keys(windows).length > 0;
            if (!isHaveWindows && !showingImage && activeContact && isChatRoute && isTabChat && e.key === "Escape") {
                store.dispatch("chat/setActiveContact", null).then(_.noop).catch(console.error);
            }
        };

        document.addEventListener("keydown", escListener);
        this.listeners.push(() => {
            document.removeEventListener("keydown", escListener);
        });
    }

    initListeners() {
        const socketStream = socketMessageStream.subscribe(({ cmd, data }) => {
            if (cmd === RESPONSE_COMMANDS.CONVERSATION_CLOSED) {
                log.debug("socket.CONVERSATION_CLOSED", data);
                this.conversationClosed(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.REFRESH_CONTACT) {
                log.debug("socket.REFRESH_CONTACT", data);
                this.refreshContact(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.NEW_CHAT_STARTED) {
                log.debug("socket.NEW_CHAT_STARTED", data);
                this.newContactConnected(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.CONVERSATION_ASSIGNED) {
                log.debug("socket.CONVERSATION_ASSIGNED", data);
                this.newContactConnected(data, true).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.MESSAGE) {
                log.debug("socket.MESSAGE", { data });
                const messageTypes = [
                    "visitor",
                    "visitor-media",
                    "visitor-location",
                    "user",
                    "user-media",
                    "user-location",
                ];
                const isMessage = messageTypes.includes(data.messageType);
                if (isMessage && !data.isTeamChat) this.newMessageReceived(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.VIEWED_CONTACT) {
                log.debug("socket.VIEWED_CONTACT", { data });
                this.viewedContact(data);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_JOINED_CHAT) {
                log.debug("socket.AGENT_JOINED_CHAT", data);
                this.agentJoinedChat(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_LEAVE_CHAT) {
                log.debug("socket.AGENT_LEAVE_CHAT", data);
                this.agentLeaveChat(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.VISITOR_UPDATED) {
                log.debug("socket.VISITOR_UPDATED", data);
                this.visitorUpdated(data);
            } else if (cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY) {
                log.debug("socket.JOIN_CHAT_IMMEDIATELY", data);
                console.log("JOIN_CHAT_IMMEDIATELY", data);
                this.joinChatImmediately(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.CHAT_AGENTS_CHANGED) {
                console.log("socket.CHAT_AGENTS_CHANGED", data);
                this.chatAgentsChanged(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.WHATSAPP_CONTACT_STATUS) {
                this.whatsappContactStatus(data);
            } else if (cmd === RESPONSE_COMMANDS.CONTACT_VISIT_PAGE_CHANGED) {
                this.contactVisitPageChanged(data);
            } else if (cmd === RESPONSE_COMMANDS.OWNER_UPDATED) {
                this.ownerChanged(data);
            } else if (cmd === RESPONSE_COMMANDS.NOTE_ADDED) {
                this.noteAdded(data);
            } else if (cmd === RESPONSE_COMMANDS.NOTE_DELETED) {
                this.noteDeleted(data);
            } else if (cmd === RESPONSE_COMMANDS.CONVERSATIONS_SUBSCRIBED) {
                const { filterHash } = data;
                chatSubscribeCheck["responseId"] = filterHash;
                console.log("chatSubscribeCheck on response", chatSubscribeCheck);
            }
        });

        const tabVisibilitySub = systemMessageStream
            .pipe(
                filter((x) => x.type === SystemMessageTypes.TAB_VISIBILITY_CHANGE),
                map((x) => x.payload)
            )
            .subscribe((payload) => {
                const { visible } = payload;
                this.tabVisibleAction(visible);
            });

        this.listeners.push(() => {
            socketStream.unsubscribe();
            tabVisibilitySub.unsubscribe();
        });
    }

    tabVisibleAction(visible) {
        if (visible) {
            this.totalUnreadCount = 0;
        } else {
            document.title = this.totalUnreadCount > 0 ? `(${this.totalUnreadCount} new message) Supsis` : "Supsis";
        }
        this.tabVisible = visible;
    }

    noteAdded(data) {
        const { contactId } = data || {};
        const contact = store.state.chat.contactLists.find((c) => c._id === contactId);
        if (contact) {
            this.updateContactMultipleKey(contactId, undefined, {
                noteCount: contact.noteCount + 1,
            });
        }
    }

    noteDeleted(data) {
        const { contactId } = data || {};
        const contact = store.state.chat.contactLists.find((c) => c._id === contactId);
        if (contact) {
            this.updateContactMultipleKey(contactId, undefined, {
                noteCount: contact.noteCount - 1,
            });
        }
    }

    ownerChanged(data) {
        const { visitorId, ownerId } = data;
        const authUser = store.state.auth.user;
        const authUserId = authUser._id;
        const isRoot = authUser.role.isRoot;
        const onlyAdminsShowsNoOwnerContacts = store.state.setting.settings.onlyAdminsShowsNoOwnerContacts;
        const contact = store.state.chat.contactLists.find((c) => c._id === visitorId);
        const isCurrentOwnerByMe = contact?.owner === authUserId;
        this.updateContactMultipleKey(visitorId, undefined, {
            owner: ownerId,
        });
        if (isCurrentOwnerByMe) {
            store.dispatch("chat/removeManualChatStartedContactIds", [visitorId]).then(_.noop).catch(console.error);
        }
        const isRootUser = store.state.auth.user?.role?.isRoot || false;
        const filters = store.getters["chat/getFittedContactListFilters"];
        let watchContact = true;
        if (ownerId) {
            if (!isRootUser && ownerId !== store.state.auth.user._id) {
                if (contact) {
                    log.warn("ownerChanged -> removeContactFromList -> Not my owner", {
                        contact,
                        isRootUser,
                        filters,
                    });
                    this.removeContactFromList(contact, "ownerChanged", true);
                    watchContact = false;
                }
            }
        } else {
            if (!isRootUser && onlyAdminsShowsNoOwnerContacts) {
                if (contact) {
                    log.warn(
                        "ownerChanged -> removeContactFromList -> No owner and onlyAdminsShowsNoOwnerContacts is true",
                        {
                            contact,
                            isRootUser,
                            filters,
                        }
                    );
                    this.removeContactFromList(contact, "ownerChanged", true);
                    watchContact = false;
                }
            }
        }
        if (watchContact) this.watchContactConversations();
    }

    contactVisitPageChanged(data) {
        log.debug("contactVisitPageChanged", data);
        const { visitorId, url } = data;
        let contact = store.state.chat.contactLists.find((c) => c._id === visitorId);
        if (!contact) {
            log.debug("contactVisitPageChanged -> Contact not exists", {
                data,
            });
            return;
        }

        this.updateContactMultipleKey(visitorId, undefined, {
            visitingPage: url,
        });
    }

    whatsappContactStatus(data) {
        store
            .dispatch("chat/setWhatsappContactStatus", {
                whatsappNumber: data.whatsappNumber,
                status: data.isActive,
                channelId: data.channelId,
            })
            .then(_.noop)
            .catch(console.error);
    }

    async newMessageReceived(message) {
        const isDefaultFilter =
            store.state.chat.contactListFilterMode === "custom" &&
            ["all-agent-inbox", null].includes(store.state.chat.selectedCustomFilterTemplate);
        log.debug("newMessageReceived", { message, isDefaultFilter });
        const unreadContactCount = store.state.chat.contactLists.filter((c) => c.unreadedCount > 0).length;
        const unreadStat = store.state.chat.contactPaginationCursor.stats.unread;
        if (unreadContactCount > unreadStat) {
            fetchStatsDebounced(100);
        } else {
            fetchStatsDebounced(3 * 1000);
        }
        if (!message.visitorId) {
            log.warn("newMessageReceived -> visitorId not exists", [message]);
            return;
        }

        let contact = store.state.chat.contactLists.find((c) => c._id === message.visitorId);
        if (!contact && !isDefaultFilter) {
            // todo burada hata var
            // if (!contact) {
            contact = await store.dispatch("chat/getContactById", {
                contactId: message.visitorId,
                cache: true,
                ignoreSource: "newMessageReceived",
            });
        }
        if (!contact) {
            log.debug("newMessageReceived -> Contact not exists", {
                message,
            });
            return;
        }

        const { uniqMessageId, message: description, asset, location, messageType, createdDate } = message;
        store.commit("chat/ACT_PENDING_MESSAGE", uniqMessageId);

        if ((messageType || "").startsWith("visitor")) {
            const user = store.state.auth.user;
            const activeContact = store.state.chat.activeContact;
            const isActiveContact = activeContact?._id === message.visitorId;
            const playRequired =
                contact.lastConversation.users.includes(user._id) &&
                ((isActiveContact && !this.tabVisible) || !isActiveContact);
            if (playRequired) playSound(RESPONSE_COMMANDS.MESSAGE);
            if (!this.tabVisible) this.totalUnreadCount += 1;
            const businessChannel = store.state.channel.channels.find((channel) => channel._id === contact.channel);
            if (businessChannel) {
                setTimeout(() => {
                    sendSocketMessage("user whatsapp-contact-status", {
                        siteId: store.state.site.site._id,
                        channelId: contact.channel,
                        whatsappNumber: contact.whatsappNumber,
                    });
                }, 200); // bu kısım mesajlar bufferlanıp gönderildiği için asenkron çalışıyor
            }
        }

        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === message.visitorId);
        if (!isAlreadyExits) this.addContactToList(contact, "newMessageReceived");

        this.updateContactMultipleKey(message.visitorId, undefined, {
            lastInteractionDate: createdDate,
            lastMessage: {
                createdDate: createdDate,
                _id: uniqMessageId,
                description,
                asset,
                location,
                type: messageType,
            },
        });
        store
            .dispatch("chat/incContactUnreadCount", {
                contactId: message.visitorId,
            })
            .then(_.noop)
            .catch(console.error);

        const translateActive = store.state.chat.autoTranslate.includes(contact.lastConversation._id);
        const userLang = store.state.auth.user.lang;
        const contactPreferedLang = contact.preferredLang;
        const isAlreadyTranslated = message.translates.find(
            ({ from, to }) => from === contactPreferedLang && to === userLang
        );
        if (translateActive && userLang !== contactPreferedLang && !isAlreadyTranslated) {
            sendSocketMessage(COMMANDS.TRANSLATE_MESSAGE, {
                siteId: store.state.site.site._id,
                to: userLang,
                data: {
                    uniqMessageId,
                    message: description,
                    baseLang: contactPreferedLang,
                    translates: message.translates,
                },
            });
        }
    }

    viewedContact(data) {
        log.debug("viewedContact", {});

        this.updateContactMultipleKey(data.contactId, undefined, {
            unreadedCount: 0,
        });
    }

    removeContactFromListIfNeeded(contactId, ignoreSource) {
        log.debug("contact.removeContactFromListIfNeeded called" + contactId + " source:" + ignoreSource);
        const contact = store.state.chat.contactLists.find((c) => c._id === contactId);
        if (contact) {
            const manualStartedContactIds = store.state.chat.manualChatStartedContactIds;
            const isManualStarted = manualStartedContactIds.includes(contactId);
            if (isManualStarted) {
                log.warn(`${contactId} is manual started contact. Not removing from contact list.`);
                return;
            }
            const filters = store.getters["chat/getFittedContactListFilters"];
            const lastChatStatusList = filters.lastChatStatusList || [];
            const filterOwners = filters.lastChatOwner || [];
            const lastStatus = _.get(contact, "lastConversation.status");
            const chatOwners = _.get(contact, "lastConversation.users", []) || [];
            if (lastChatStatusList.length === 0) return;
            const isStatusNotMatched = !!lastStatus
                ? !lastChatStatusList.includes(null) && lastChatStatusList.every((s) => s !== lastStatus)
                : false;
            const isOwnerMatched =
                chatOwners.some((userId) => {
                    return filterOwners.map((x) => x.value).includes(userId);
                }) ||
                (filterOwners.map((x) => x.value).includes("none") && chatOwners.length === 0) ||
                filterOwners.length === 0;
            log.debug("contact.removeContactFromListIfNeeded " + contactId + " source:" + ignoreSource, {
                isStatusNotMatched,
                isOwnerMatched,
                lastChatStatusList,
                lastStatus,
                ownerCheck: {
                    filterOwners,
                    chatOwners,
                    isOwnerMatched,
                },
            });
            if (isStatusNotMatched || !isOwnerMatched) {
                this.removeContactFromList(contact, ignoreSource);
                fetchStatsDebounced(100);
            }
        }
    }

    updateContactMultipleKey(contactId, conversationId, set) {
        log.debug(`updateContactMultipleKey contact=${contactId} chat=${conversationId}`, set);
        const contact = store.state.chat.contactLists.find(
            (c) => c._id === contactId || c.lastConversation?._id === conversationId
        );
        if (contact) {
            store
                .dispatch("chat/updateMultipleKeyContact", {
                    contactId,
                    conversationId,
                    set,
                })
                .then(_.noop)
                .catch(console.error);
            this.removeContactFromListIfNeeded(contact._id, "updateContactMultipleKey");
        }
    }

    async agentJoinedChat(data) {
        log.debug("agentJoinedChat", data);
        let contact = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!contact)
            contact = await store.dispatch("chat/getContactById", {
                contactId: data.contactId,
                ignoreSource: "agentJoinedChat",
            });
        if (!contact) {
            log.error("Not should be here. agentJoinedChat -> Contact not exists", {});
            return;
        }
        const users = contact.lastConversation.users.concat(data.userId);
        contact.lastConversation.users = _.uniq([...contact.lastConversation.users, data.userId]);
        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        const isShowingContact = await store.dispatch("chat/isShowingContactList", data.contactId);
        if (!isAlreadyExits && isShowingContact) {
            this.addContactToList(contact, "agentJoinedChat");
        }

        this.updateContactMultipleKey(data.contactId, undefined, {
            lastConversation: {
                ...contact.lastConversation,
                users: _.uniq(users),
                status: "active",
            },
        });
        const status = store.state.chat.contactLists.find((c) => c._id === data.contactId)?.lastConversation?.status;
        log.debug(
            `contact. agentJoinedChat after update -> ${data.contactId} status: ${status} old=${contact.lastConversation.status}`
        );

        this.removeContactFromListIfNeeded(contact._id, "agentJoinedChat");
        if (data.userId === store.state.auth.user._id && (data.isAutoAssign || data.forward)) {
            playSound(RESPONSE_COMMANDS.CONVERSATION_ASSIGNED);
        }
    }

    async agentLeaveChat(data) {
        log.debug("agentLeaveChat", data);
        let contact = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!contact)
            contact = await store.dispatch("chat/getContactById", {
                contactId: data.contactId,
                ignoreSource: "agentLeaveChat",
            });
        if (!contact) {
            log.error("Not should be here. agentJoinedChat -> Contact not exists", {});
            return;
        }
        const users = contact.lastConversation.users;
        const filteredUsers = users.filter((u) => u !== data.userId);
        contact.lastConversation.users = filteredUsers;

        this.updateContactMultipleKey(undefined, data.conversationId, {
            lastConversation: {
                ...contact.lastConversation,
                users: _.uniq(filteredUsers),
            },
        });

        const isAlreadyExits = store.state.chat.contactLists.find((c) => c._id === data.contactId);
        if (!isAlreadyExits) this.addContactToList(contact, "agentLeaveChat");
    }

    async conversationClosed(data) {
        log.debug("conversationClosed", data);
        const contact = store.state.chat.contactLists.find((c) => c.lastConversation._id === data.conversationId);
        const activeContact = store.state.chat.activeContact;
        if (!contact) {
            log.debug("conversationClosed -> Contact not exists", {
                data,
            });
            return;
        }
        store.commit(
            "chat/SET_MANUAL_CHAT_STARTED_CONTACT_IDS",
            store.state.chat.manualChatStartedContactIds.filter((cId) => cId !== contact._id)
        );
        store.dispatch("chat/unWatchContactConversation", contact).then(_.noop).catch(console.error);

        this.updateContactMultipleKey(undefined, data.conversationId, {
            lastConversation: {
                ...contact.lastConversation,
                status: "destroyed",
            },
        });

        if (activeContact && activeContact._id === contact._id) {
            store.dispatch("chat/setActiveContact", null).then(_.noop).catch(console.error);
        }

        this.removeContactFromListIfNeeded(contact._id, "conversationClosed");
    }

    async newContactConnected(data, refresh = true) {
        let isShowingContactList = await store.dispatch("chat/isShowingContactList", data.visitor._id);
        if (data.isManuelChatStart && data.connectingUserId === store.state.auth.user._id) {
            isShowingContactList = true;
            refresh = true;
        }
        log.debug("newContactConnected " + data.visitor._id, data, "isShowingContactList=" + isShowingContactList);
        if (isShowingContactList) {
            playSound(RESPONSE_COMMANDS.NEW_CHAT_STARTED);
            let contact = store.state.chat.contactLists.find((c) => c._id === data._id);
            if (refresh || !contact) {
                contact = await store.dispatch("chat/getContactById", {
                    contactId: data.visitor._id,
                    cache: false,
                    ignoreSource: "newContactConnected",
                    updateList: true,
                });
            }

            if (contact) {
                this.updateContactMultipleKey(contact._id, undefined, {
                    lastConversation: contact.lastConversation,
                    inited: true,
                });
            }
            this.addContactToList(
                {
                    ...contact,
                    lastInteractionDate: new Date().toISOString(),
                },
                "newContactConnected"
            );
            this.watchContactConversations([contact.lastConversation._id]);
        }
    }

    async refreshContact(data) {
        const { contactId } = data;
        const isShowingContactList = await store.dispatch("chat/isShowingContactList", contactId);

        log.debug("newContactConnected " + contactId, "isShowingContactList=" + isShowingContactList);
        if (isShowingContactList) {
            const contact = await store.dispatch("chat/getContactById", {
                contactId: contactId,
                cache: false,
                ignoreSource: "refreshContact",
                updateList: true,
            });

            this.addContactToList(
                {
                    ...contact,
                    lastInteractionDate: new Date().toISOString(),
                },
                "refreshContact"
            );
            this.watchContactConversations([contact.lastConversation._id]);
        } else {
            const foundContact = store.state.chat.contactLists.find((c) => c._id === contactId);
            if (foundContact) {
                this.removeContactFromList(foundContact, "refreshContact");
            }
        }
    }

    async chatAgentsChanged({ contactId, conversationId }) {
        const isShowingContactList = await store.dispatch("chat/isShowingContactList", contactId);

        log.debug("chatAgentsChanged " + contactId, "isShowingContactList=" + isShowingContactList);
        if (isShowingContactList) {
            // playSound(RESPONSE_COMMANDS.NEW_CHAT_STARTED);

            let contact = await store.dispatch("chat/getContactById", {
                contactId,
                cache: false,
                ignoreSource: "chatAgentsChanged",
                updateList: true,
            });

            if (contact) {
                this.updateContactMultipleKey(contact._id, undefined, {
                    lastConversation: contact.lastConversation,
                    inited: true,
                });
            }
            this.addContactToList(
                {
                    ...contact,
                    lastInteractionDate: new Date().toISOString(),
                },
                "newContactConnected"
            );
            this.watchContactConversations([conversationId]);
        }
    }

    addContactToList(contact, ignoreSource) {
        const validTemplates = autoRefreshTemplates;
        const selectedCustomFilterTemplate = store.state.chat.selectedCustomFilterTemplate;
        const isCustomMode = store.state.chat.contactListFilterMode === "custom";
        if (!validTemplates.includes(selectedCustomFilterTemplate) && isCustomMode) {
            log.debug("addContactToList -> Archive mode active", {
                selectedCustomFilterTemplate,
            });
            return;
        }

        const searchQuery = store.state.chat.contactListFilters.query;
        if (searchQuery && searchQuery?.length > 0) {
            log.debug("addContactToList -> Query search mode active", {});
            return;
        }

        log.debug("contact.add", contact._id, " source=" + ignoreSource, {
            contact,
            filters: store.getters["chat/getFittedContactListFilters"],
            user: store.state.auth.user,
        });

        const isRootUser = store.state.auth.user?.role?.isRoot || false;
        const filters = store.getters["chat/getFittedContactListFilters"];
        const onlyAdminsShowsNoOwnerContacts = store.state.setting.settings.onlyAdminsShowsNoOwnerContacts || false;
        if (contact.owner) {
            if (!isRootUser && contact.owner !== store.state.auth.user._id) {
                log.warn("addContactToList -> not my owner", {
                    contact,
                    isRootUser,
                    filters,
                });
                return;
            }
        } else {
            if (!isRootUser && onlyAdminsShowsNoOwnerContacts) {
                log.warn("addContactToList -> No owner and onlyAdminsShowsNoOwnerContacts is true", {
                    contact,
                    isRootUser,
                    filters,
                });
                return false;
            }
        }
        const length = store.state.chat.contactLists.length + 1;
        const limit = store.state.chat.contactPaginationCursor.limit;
        const isDeleteRequired = length > limit;
        store
            .dispatch("chat/setContactList", [contact, ...store.state.chat.contactLists])
            .then(_.noop)
            .catch(console.error);
        if (isDeleteRequired) {
            const lastItem = store.state.chat.contactLists[store.state.chat.contactLists.length - 1];
            const activeContact = store.state.chat.activeContact;
            if (lastItem && lastItem._id !== activeContact?._id) this.removeContactFromList(lastItem);
        }
        fetchStatsDebounced(100);
        if (store.state.chat.activeContact?._id === contact._id) {
            store.dispatch("chat/setActiveContact", contact).then(_.noop).catch(console.error);
        }
        //this.removeContactFromListIfNeeded(contact._id, ignoreSource);
    }

    removeContactFromList(contact, ignoreSource) {
        const validTemplates = autoRefreshTemplates;
        const selectedCustomFilterTemplate = store.state.chat.selectedCustomFilterTemplate;
        const isCustomMode = store.state.chat.contactListFilterMode === "custom";
        if (!validTemplates.includes(selectedCustomFilterTemplate) && isCustomMode) {
            log.debug("removeContactFromList -> Archive mode active", {
                selectedCustomFilterTemplate,
            });
            return;
        }

        log.debug(`contact.remove ${contact._id} source=${ignoreSource}`);
        store
            .dispatch(
                "chat/setContactList",
                store.state.chat.contactLists.filter((c) => c._id !== contact._id)
            )
            .then(_.noop)
            .catch(console.error);
        // store.dispatch(
        //     "chat/setContacts",
        //     store.state.chat.contacts.filter((c) => c._id !== contact._id)
        // );
        if (store.state.chat.activeContact?._id === contact._id) {
            store.dispatch("chat/setActiveContact", null).then(_.noop).catch(console.error);
        }
        store.dispatch("chat/unWatchContactConversation", contact).then(_.noop).catch(console.error);
    }

    visitorUpdated(data) {
        log.debug("visitorUpdated", data);

        const contact = store.state.chat.contactLists.find((c) => c._id === data.visitor._id);
        if (!contact) {
            log.debug("visitorUpdated -> Contact not exists", {
                data,
            });
            return;
        }

        const unExceptedFields = ["lastConversation", "inited", "lastMessage", "lastInteractionDate"];
        const updatedFields = Object.keys(contact)
            .filter((key) => !unExceptedFields.includes(key))
            .reduce((acc, key) => {
                if (data.visitor[key] !== undefined && contact[key] !== data.visitor[key]) {
                    if (key === "tags") {
                        data.visitor.tags = data.visitor.tags.map((tag) => tag._id);
                    } else if (key === "fullname") {
                        data.visitor.fullname = data.visitor.fullname || contact.lastConversation.fullname;
                    } else if (key === "email") {
                        data.visitor.email = data.visitor.email || contact.lastConversation.email;
                    }
                    acc[key] = data.visitor[key];
                }
                return acc;
            }, {});

        this.updateContactMultipleKey(data.visitor._id, undefined, updatedFields);
    }

    joinLostChats() {
        log.debug("joinLostChats", {});
        const authUser = store.state.auth.user;
        const site = store.state.site.site;
        const myChatIds = store.state.chat.myChatIds;
        if (myChatIds.length > 0) {
            sendSocketMessage("user join-lost-conversations", {
                siteId: site._id,
                userId: authUser._id,
                ids: myChatIds,
                observing: false,
            });
        }
    }

    static async startChatImmediately(data) {
        const fn = () => {
            log.debug("startChatImmediately", data);
            const site = store.state.site.site;
            const user = store.state.auth.user;
            // Kullanıcının seçilen kanala erişim yetkisi kontrolü
            const selectedChannel = store.state.channel.channels.find((channel) => channel._id === data.channelId);
            if (data.channelId && (!selectedChannel || !ChatWatcher.isChannelAvailable(selectedChannel))) {
                message.error("Seçilen kanala erişim yetkiniz bulunmamaktadır.");
                return;
            }

            const hideMessage = message.loading(i18n.t("wp.chatStartingWait"), 0);
            sendSocketMessage(COMMANDS.USER_START_CHAT, {
                siteId: site._id,
                userId: user._id,
                ...data,
            });

            socketMessageStream
                .pipe(
                    filter((x) => x.cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY),
                    timeout(5000),
                    first()
                )
                .toPromise()
                .then(async (x) => {
                    message.success(i18n.t("wp.chatStarted"));
                })
                .catch((err) => {
                    message.error(i18n.t("wp.chatNotStarted"));
                })
                .finally(() => {
                    hideMessage();
                });
        };
        if (!data.channelId) {
            feature.create().beforeVisitorStartChat(fn);
        } else {
            fn();
        }
    }

    static async observeContact(contactId) {
        const hideMessage = message.loading("Sohbet'e bağlanılıyor...", 0);
        const isShowingContact = await store.dispatch("chat/isShowingContact", contactId);
        if (!isShowingContact) {
            message.error("Bu sohbeti görmek için yeterli yetkiniz bulunmamaktadır.");
            hideMessage();
            return;
        }
        const contact = await store.dispatch("chat/getContactById", {
            contactId,
            cache: true,
            ignoreSource: "observeContact",
        });

        if (!contact) {
            message.error("Sohbet bulunamadı.");
            hideMessage();
            return;
        }

        store.commit("chat/SET_MANUAL_CHAT_STARTED_CONTACT_IDS", [
            ...store.state.chat.manualChatStartedContactIds,
            contact._id,
        ]);
        await store.dispatch("chat/setContactList", [contact, ...store.state.chat.contactLists]);
        await store.dispatch("chat/setActiveContact", contact);
        router.push("/chat?tabIndex=0");
        watchContactConversations([contact.lastConversation._id]);
        hideMessage();
    }

    static isChannelAvailable(channel) {
        const authUser = store.state.auth.user;
        const isSuperAdmin = authUser.isSuperAdmin || authUser.role?.isRoot || authUser.isOwner;

        if (isSuperAdmin) return true;

        const visibility = channel.visibility || { users: [], departments: [] };
        visibility.users = Array.isArray(visibility.users) ? visibility.users : [];
        visibility.departments = Array.isArray(visibility.departments) ? visibility.departments : [];

        const userDepartmentIds = Array.isArray(authUser.subjects)
            ? authUser.subjects.map((x) => (typeof x === "object" ? x._id.toString() : x.toString()))
            : [];

        const isPublic = visibility.users.length === 0 && visibility.departments.length === 0;
        if (isPublic) return true;

        const isVisibleToUser = visibility.users.some((x) => x.toString() === authUser._id.toString());
        const isVisibleToDepartment = visibility.departments.some(
            (x) => userDepartmentIds.includes(x.toString()) || userDepartmentIds.length === 0
        );

        return isVisibleToUser || isVisibleToDepartment;
    }

    async joinChatImmediately(data) {
        log.debug("joinChatImmediately", data);
        const user = store.state.auth.user;
        const { contactId, conversationId } = data;

        let contact = store.state.chat.contacts.find((c) => c._id === contactId);
        if (!contact) {
            contact = await store.dispatch("chat/getContactById", {
                contactId,
                cache: false,
                updateList: true,
                ignoreSource: "joinChatImmediately",
            });
        }

        if (!contact) {
            log.error("Not should be here. joinChatImmediately -> Contact not exists", {});
            return;
        }

        const isMyChat = (contact.lastConversation?.users || []).includes(user._id);
        if (!isMyChat) {
            sendSocketMessage("user join-conversation", {
                userId: user._id,
                conversationId,
                siteId: store.state.site.site._id,
                reconnect: false,
                autoAssign: false,
                watchMode: false,
                startedFromAgent: true,
            });
        }

        store.commit("chat/SET_MANUAL_CHAT_STARTED_CONTACT_IDS", [
            ...store.state.chat.manualChatStartedContactIds,
            contact._id,
        ]);
        store
            .dispatch("chat/setContactList", [contact, ...store.state.chat.contactLists])
            .then(_.noop)
            .catch(console.error);
        store.dispatch("chat/setActiveContact", contact).then(_.noop).catch(console.error);
        router.push("/chat?tabIndex=0");
        watchContactConversations([conversationId]);
    }

    reSync() {
        log.debug("reSync", {});
        if (!this.inited) return;
        log.debug("ChatWatcher reSync started.");
        this.listeners.forEach((listener) => listener());
        this.initListeners();
        this.joinLostChats();
        fetchContactListDebounced(false, 3, 0).then(_.noop).catch(console.error);
        fetchStatsDebounced(0);
    }
}
